import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { ClassesApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi, getApiErrorMessage } from "@/lib/utils";
import { useToast } from "@/components/feedback";

export const useClassBulkDelete = () => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { mutate, isPending } = useMutation({
    mutationFn: async (classIds: number[]) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(ClassesApi, accessToken);
      await Promise.all(
        classIds.map((id) => service.apiClassesIdDelete({ id })),
      );
    },
    onSuccess: async (_data, classIds) => {
      for (const classId of classIds) {
        queryClient.removeQueries({ queryKey: [QueryKeys.Class, classId] });
      }

      await queryClient.invalidateQueries({
        predicate: (query) => {
          return (
            Array.isArray(query.queryKey) &&
            (query.queryKey[0] === QueryKeys.Classes ||
              query.queryKey[0] === QueryKeys.Class)
          );
        },
        refetchType: "all",
      });

      const count = classIds.length;
      toast({
        title: "Classes deleted",
        description: `${count} class${count === 1 ? "" : "es"} deleted successfully!`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error: unknown) => {
      const errorMessage = await getApiErrorMessage(
        error,
        "Failed to delete classes",
      );
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending };
};
